// The per-site hash chain. See docs/spec/events.md §3.
import { concat, fromHex, isHex64, sha256Hex } from "./bytes.ts";
import { canonicalBytes, canonicalHash } from "./canonical.ts";
import { verifyCanonical } from "./keys.ts";
import { validatePreImage, type PreImage, type SealedBody, type SealedEvent } from "./schema.ts";

const LINK_TAG = new Uint8Array([0x02]);

export const genesisHash = (siteId: string) => canonicalHash({ v: 1, kind: "genesis", site_id: siteId });

export const bodyHash = (body: SealedBody) => sha256Hex(canonicalBytes(body));

export function linkHash(prevHash: string, bodyHashHex: string): Promise<string> {
  if (!isHex64(prevHash) || !isHex64(bodyHashHex)) throw new Error("link inputs must be 64-char hex");
  return sha256Hex(concat(LINK_TAG, fromHex(prevHash), fromHex(bodyHashHex)));
}

export async function seal(args: {
  prevHash: string;
  seq: number;
  serverTs: number;
  clockOffsetMs: number;
  pre: PreImage;
  deviceSig: string;
}): Promise<SealedEvent> {
  const body: SealedBody = {
    v: 1, seq: args.seq, server_ts: args.serverTs, clock_offset_ms: args.clockOffsetMs, pre: args.pre, device_sig: args.deviceSig,
  };
  const bh = await bodyHash(body);
  return { ...body, prev_hash: args.prevHash, body_hash: bh, event_hash: await linkHash(args.prevHash, bh) };
}

export const bodyOf = (e: SealedEvent): SealedBody => ({
  v: e.v, seq: e.seq, server_ts: e.server_ts, clock_offset_ms: e.clock_offset_ms, pre: e.pre, device_sig: e.device_sig,
});

/**
 * A chain segment as carried in an export or evidence pack. Full records are verified end to
 * end; `link` entries stand for other children's events and carry only their body hash.
 */
export type SegmentEntry =
  | { kind: "event"; event: SealedEvent }
  | { kind: "link"; seq: number; body_hash: string; event_hash: string };

export interface VerifyIssue { seq: number | null; problem: string }

export interface VerifyOptions {
  siteId: string;
  /** Device public keys (raw base64url) by device id; events from unknown devices fail. */
  devices: Record<string, string>;
  /** Hash preceding the first entry. Defaults to the site genesis when the segment starts at seq 1. */
  startPrevHash?: string;
}

export async function verifySegment(entries: SegmentEntry[], opts: VerifyOptions): Promise<{ ok: boolean; head: string | null; issues: VerifyIssue[] }> {
  const issues: VerifyIssue[] = [];
  if (entries.length === 0) return { ok: true, head: opts.startPrevHash ?? null, issues };
  const firstSeq = entries[0]!.kind === "event" ? entries[0]!.event.seq : entries[0]!.seq;
  let prev = opts.startPrevHash ?? (firstSeq === 1 ? await genesisHash(opts.siteId) : null);
  if (prev === null) return { ok: false, head: null, issues: [{ seq: firstSeq, problem: "segment does not start at seq 1 and no start hash was given" }] };
  let expectSeq = firstSeq;

  for (const e of entries) {
    const seq = e.kind === "event" ? e.event.seq : e.seq;
    if (seq !== expectSeq) issues.push({ seq, problem: `expected seq ${expectSeq}` });
    expectSeq = seq + 1;
    if (e.kind === "link") {
      if (e.event_hash !== (await linkHash(prev, e.body_hash))) issues.push({ seq, problem: "link hash mismatch" });
      prev = e.event_hash;
      continue;
    }
    const ev = e.event;
    if (ev.prev_hash !== prev) issues.push({ seq, problem: "prev_hash does not match the previous event" });
    const bh = await bodyHash(bodyOf(ev));
    if (bh !== ev.body_hash) issues.push({ seq, problem: "body hash mismatch (record altered)" });
    if ((await linkHash(prev, bh)) !== ev.event_hash) issues.push({ seq, problem: "event hash mismatch" });
    const schema = validatePreImage(ev.pre);
    if (schema.length) issues.push({ seq, problem: `schema: ${schema.join("; ")}` });
    if (ev.pre.site_id !== opts.siteId) issues.push({ seq, problem: "event belongs to another site" });
    const pub = opts.devices[ev.pre.device_id];
    if (!pub) issues.push({ seq, problem: `unknown device ${ev.pre.device_id}` });
    else if (!(await verifyCanonical(pub, ev.pre, ev.device_sig))) issues.push({ seq, problem: "device signature invalid" });
    prev = ev.event_hash;
  }
  return { ok: issues.length === 0, head: prev, issues };
}
